import { Box, HStack, Icon, Text, Tr, VStack } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import type { ElementType, ReactNode } from 'react';
import { TbChevronRight } from 'react-icons/tb';
import Pagination from '../../../../../components/Pagination';
import PartBayButton from '../../../../../components/PartBayButton';
import {
    PARTBAY_BLUE,
    SETTINGS_TABLE_GUTTER,
    SettingsInlineNumberEdit,
    SettingsPageActions,
    SettingsRowActions,
    SettingsTable,
    SettingsTableFooter,
    SettingsTableTabs,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
} from '../../../../../components/settings/primitives';
import {
    formatLkqPricingSubCompanySummary,
    type LkqPricingRowValues,
    type LkqPricingRule,
} from '../../../../../utils/lkqPricingRows';
import { decimalToWholePercent, wholePercentToDecimal } from '../shared';
import { InlinePricingStatesCell, InlinePricingVendorCell } from './PricingInlineCells';

export interface PricingTableTab {
    /** Stable id; also what the tab selector and the row's URL carry. */
    id: string;
    label: string;
    rows: LkqPricingRule[];
    /** Header for the percentage column, e.g. "% markup". */
    percentColumnLabel: string;
    /** Shown in the table body when the tab has no rows. */
    emptyText: string;
    addButtonText: string;
    onAdd: () => void;
    /** Opens one row's full-screen editor. */
    onEditRow: (rule: LkqPricingRule) => void;
    /** Restates one row from an inline cell edit. */
    onSaveRow: (rule: LkqPricingRule, values: LkqPricingRowValues) => void;
    onRemoveRow: (ruleKey: string) => void;
    /** Defaults panel for this tab, between the tab selector and the table. */
    defaults?: ReactNode;
    /** Aria label suffix for the row editors, e.g. "markup". */
    rowNoun: string;
}

export interface PricingTableSectionProps {
    tabs: PricingTableTab[];
    /** States the org operates in, for the states cell. */
    organizationStates: string[];
    isSaving: boolean;
}

/**
 * Full-screen table for the invoicing pricing rows: one tab per line of
 * business (used and aftermarket parts, sublet), the tab's defaults directly
 * under the selector, and the rows themselves in the page's table.
 *
 * The rows are the discount flag table's rows — the same vendor, states and
 * percentage cells edited in place, the company name opening the row's full
 * editor — so the three pricing screens read and behave identically. Markups
 * and discounts both render this; only their columns, copy and defaults differ.
 */
const PricingTableSection = ({
    tabs,
    organizationStates,
    isSaving,
}: PricingTableSectionProps) => {
    const [activeTabId, setActiveTabId] = useState(tabs[0]?.id);
    const [page, setPage] = useState(1);
    const { scrollRef, pageSize } = useDynamicPageSize();

    const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
    const rows = activeTab.rows;
    const totalPages = Math.ceil(rows.length / pageSize);

    // The page size follows the viewport, so a resize can shrink the page
    // count; clamp so the user is never stranded on a now-empty page.
    useEffect(() => {
        setPage((prev) => Math.min(prev, Math.max(1, totalPages)));
    }, [totalPages]);

    const pageRows = useMemo(() => {
        const start = (page - 1) * pageSize;
        return rows.slice(start, start + pageSize);
    }, [rows, page, pageSize]);

    const handleChangeTab = (id: string) => {
        setActiveTabId(id);
        setPage(1);
    };

    /** One inline cell's change, restated as the row's full set of values. */
    const saveRow = (rule: LkqPricingRule, patch: Partial<LkqPricingRowValues>) => {
        activeTab.onSaveRow(rule, {
            supplierId: patch.supplierId ?? rule.supplierId,
            supplierName: patch.supplierName ?? rule.supplierName,
            partKind: patch.partKind ?? rule.partKind,
            make: patch.make ?? rule.make,
            states: patch.states ?? rule.states,
            percentage: patch.percentage ?? rule.percentage,
        });
    };

    return (
        <>
            <SettingsPageActions>
                <PartBayButton
                    colorScheme="partbay-blue"
                    variant="solid"
                    size="sm"
                    text={activeTab.addButtonText}
                    onClick={activeTab.onAdd}
                    isDisabled={isSaving}
                />
            </SettingsPageActions>

            <SettingsTableTabs
                tabs={tabs.map((tab) => ({
                    id: tab.id,
                    label: tab.label,
                    count: tab.rows.length,
                }))}
                value={activeTab.id}
                onChange={handleChangeTab}
            />

            {activeTab.defaults && (
                <Box px={SETTINGS_TABLE_GUTTER} pb={4} flexShrink={0}>
                    {activeTab.defaults}
                </Box>
            )}

            <SettingsTable
                scrollRef={scrollRef}
                head={
                    <>
                        <SettingsTh minW="200px">Insurance company</SettingsTh>
                        <SettingsTh minW="160px">Vendor name</SettingsTh>
                        <SettingsTh minW="140px">Part kind</SettingsTh>
                        <SettingsTh minW="120px">Make</SettingsTh>
                        <SettingsTh minW="160px">States</SettingsTh>
                        <SettingsTh minW="140px">{activeTab.percentColumnLabel}</SettingsTh>
                        <SettingsTh textAlign="center" w="100px">
                            Actions
                        </SettingsTh>
                    </>
                }
            >
                {rows.length === 0 ? (
                    <Tr>
                        <SettingsTd colSpan={7} py={6} color="gray.500">
                            {activeTab.emptyText}
                        </SettingsTd>
                    </Tr>
                ) : (
                    pageRows.map((rule) => (
                        // role="group" powers the chevron that fades in on
                        // hover for every editable cell in the row.
                        <Tr key={rule.ruleKey} role="group" _hover={{ bg: 'gray.50' }}>
                            <SettingsTd>
                                {/* The company name is the way into the full
                                    editor; every other column edits in place. A
                                    branched sub company carries its parent in
                                    front of it, and a primary lists what rides
                                    along. */}
                                <VStack align="flex-start" spacing={0.5}>
                                    <HStack spacing={1} minW={0}>
                                        {rule.parentInsuranceCompanyName && (
                                            <>
                                                <Text
                                                    fontSize="sm"
                                                    color="gray.500"
                                                    noOfLines={1}
                                                >
                                                    {rule.parentInsuranceCompanyName}
                                                </Text>
                                                <Icon
                                                    as={TbChevronRight as ElementType}
                                                    boxSize={3.5}
                                                    color="gray.400"
                                                    flexShrink={0}
                                                />
                                            </>
                                        )}
                                        <Text
                                            as="button"
                                            fontSize="sm"
                                            textAlign="left"
                                            color="gray.800"
                                            _hover={{
                                                color: PARTBAY_BLUE,
                                                textDecoration: 'underline',
                                            }}
                                            onClick={() => activeTab.onEditRow(rule)}
                                        >
                                            {rule.insuranceCompanyName}
                                        </Text>
                                    </HStack>
                                    {rule.includedCompanies.length > 0 && (
                                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                            {formatLkqPricingSubCompanySummary(rule)}
                                        </Text>
                                    )}
                                </VStack>
                            </SettingsTd>
                            <SettingsTd>
                                <InlinePricingVendorCell
                                    value={{
                                        supplierId: rule.supplierId,
                                        supplierName: rule.supplierName,
                                    }}
                                    ariaLabel={`Edit ${rule.insuranceCompanyName} vendor`}
                                    isDisabled={isSaving}
                                    onSave={(next) => saveRow(rule, next)}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <Text fontSize="sm" color="gray.700">
                                    {rule.partKind || 'Used & aftermarket'}
                                </Text>
                            </SettingsTd>
                            <SettingsTd>
                                <Text fontSize="sm" color="gray.700" textTransform="capitalize">
                                    {rule.make || 'All'}
                                </Text>
                            </SettingsTd>
                            <SettingsTd>
                                <InlinePricingStatesCell
                                    value={rule.states}
                                    options={organizationStates}
                                    ariaLabel={`Edit ${rule.insuranceCompanyName} states`}
                                    isDisabled={isSaving}
                                    onSave={(states) => saveRow(rule, { states })}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <SettingsInlineNumberEdit
                                    display={
                                        <Text fontSize="sm" color="gray.700">
                                            {decimalToWholePercent(rule.percentage)}%
                                        </Text>
                                    }
                                    value={decimalToWholePercent(rule.percentage)}
                                    unit="%"
                                    min={0}
                                    max={100}
                                    placeholder="0"
                                    // A percentage needs a couple of characters,
                                    // so the editor stays narrow instead of
                                    // matching the cell.
                                    width="150px"
                                    minW="150px"
                                    isDisabled={isSaving}
                                    ariaLabel={`Edit ${rule.insuranceCompanyName} ${activeTab.rowNoun}`}
                                    onSave={(next) => {
                                        // Clearing the field would leave a row
                                        // with no percentage, so an empty entry
                                        // is ignored.
                                        if (next === null) return;
                                        saveRow(rule, { percentage: wholePercentToDecimal(next) });
                                    }}
                                />
                            </SettingsTd>
                            <SettingsTd textAlign="center">
                                <SettingsRowActions
                                    onEdit={() => activeTab.onEditRow(rule)}
                                    onDelete={() => activeTab.onRemoveRow(rule.ruleKey)}
                                    editLabel={`Edit ${activeTab.rowNoun} row`}
                                    deleteLabel={`Remove ${activeTab.rowNoun} row`}
                                    isEditDisabled={isSaving}
                                    isDeleteDisabled={isSaving}
                                />
                            </SettingsTd>
                        </Tr>
                    ))
                )}
            </SettingsTable>

            <SettingsTableFooter>
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    py={0}
                />
            </SettingsTableFooter>
        </>
    );
};

export default PricingTableSection;
